import {useState, useCallback} from 'react'
import {useTranslation} from 'react-i18next'
import {authClient} from '../auth-client'
import {
  AuthLayout, AuthLink, ErrorBanner, fieldClass, primaryButtonClass, secondaryButtonClass
} from './auth-layout'

function MailIcon() {
  return (
    <svg
      width='28'
      height='28'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.75'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <rect x='3' y='5' width='18' height='14' rx='2' />
      <path d='m3 7 9 6 9-6' />
    </svg>
  )
}

export function SignUpPage() {
  const {t} = useTranslation()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | undefined>()
  const [done, setDone] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [resending, setResending] = useState(false)
  const [resent, setResent] = useState(false)
  const [resendError, setResendError] = useState<string | undefined>()

  const handleSubmit = useCallback(async (event: React.SyntheticEvent) => {
    event.preventDefault()
    setSubmitting(true)
    setError(undefined)

    try {
      const {error} = await authClient.signUp.email({
        email,
        password,
        name: `${firstName} ${lastName}`.trim(),
        firstName,
        lastName,
        callbackURL: `${globalThis.location.origin}/`
      })

      if (error) {
        setError(error.code === 'USER_ALREADY_EXISTS'
          ? t('auth.signUp.errorUserExists')
          : t('auth.signUp.errorDefault'))
        return
      }

      setDone(true)
    } catch {
      // Network/CORS failure: the request never reached a verdict. Surface a
      // generic error instead of leaving the button stuck on "submitting".
      setError(t('auth.signUp.errorDefault'))
    } finally {
      setSubmitting(false)
    }
  }, [firstName, lastName, email, password, t])

  const handleResend = useCallback(async () => {
    setResending(true)
    setResent(false)
    setResendError(undefined)

    try {
      await authClient.sendVerificationEmail({email, callbackURL: `${globalThis.location.origin}/`})
      setResent(true)
    } catch {
      setResendError(t('auth.signUp.resendError'))
    } finally {
      setResending(false)
    }
  }, [email, t])

  if (done) {
    return (
      <div className='flex w-full flex-1 items-center justify-center px-4 py-12'>
        <div className='flex w-full max-w-sm flex-col items-center gap-5 text-center'>
          <span className='flex h-16 w-16 items-center justify-center rounded-full bg-secondary text-secondary-foreground'>
            <MailIcon />
          </span>
          <div className='flex flex-col gap-2'>
            <h1 className='text-xl font-semibold tracking-tight'>{t('auth.signUp.checkEmailTitle')}</h1>
            <p className='text-sm text-muted-foreground'>{t('auth.signUp.checkEmailMessage', {email})}</p>
          </div>
          {resendError && <ErrorBanner message={resendError} />}
          {resent && <p className='text-sm font-medium text-secondary-foreground'>{t('auth.signUp.resent')}</p>}
          <button type='button' disabled={resending} className={secondaryButtonClass} onClick={handleResend}>
            {resending ? t('auth.signUp.resending') : t('auth.signUp.resend')}
          </button>
          <AuthLink to='/sign-in'>{t('auth.signUp.backToSignIn')}</AuthLink>
        </div>
      </div>
    )
  }

  return (
    <AuthLayout
      title={t('auth.signUp.title')}
      footer={<>{t('auth.signUp.haveAccount')} <AuthLink to='/sign-in'>{t('auth.signUp.signInLink')}</AuthLink></>}
    >
      <form onSubmit={handleSubmit} className='flex flex-col gap-4'>
        {error && <ErrorBanner message={error} />}
        <div className='flex gap-3'>
          <input
            type='text' placeholder={t('auth.fields.firstName')} value={firstName}
            required autoFocus
            autoComplete='given-name' className={fieldClass}
            onChange={e => {
              setFirstName(e.target.value)
            }}
          />
          <input
            type='text' placeholder={t('auth.fields.lastName')} value={lastName}
            required
            autoComplete='family-name' className={fieldClass}
            onChange={e => {
              setLastName(e.target.value)
            }}
          />
        </div>
        <input
          type='email' placeholder={t('auth.fields.email')} value={email}
          required
          autoComplete='email' className={fieldClass}
          onChange={e => {
            setEmail(e.target.value)
          }}
        />
        <input
          type='password' placeholder={t('auth.fields.password')} value={password}
          required minLength={8}
          autoComplete='new-password' className={fieldClass}
          onChange={e => {
            setPassword(e.target.value)
          }}
        />
        <button type='submit' disabled={submitting} className={primaryButtonClass}>
          {submitting ? t('auth.signUp.submitting') : t('auth.signUp.submit')}
        </button>
      </form>
    </AuthLayout>
  )
}
